import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
} from "react";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import "./PrescriptionPreview.scss";

import { formatDateIST } from "../../pages/doctor-console/Utils/UtilsFunctions";
import { BrandingContext } from "../../BrandingProvider";
import NavLogoFooter from "../../assets/images/logo/NavLogo_new.png";
import templatePdfUrl from "./prescription_template.pdf";
import { usePermissions } from "../../pages/login/PermissionContext";

const PrescriptionPdf = forwardRef(
  (
    {
      selectedSectionsData,
      historyDetails,
      doctorData,
      departments = [],
      vitals,
      dentalData,
      patientData,
      bloodGroup,
      selectedContext,
      selectedContextDisplay,
    },
    ref
  ) => {
    console.log("DoctorData from preview page", doctorData);
    console.log(
      "Selected Section Data from preview page",
      selectedSectionsData
    );
    console.log("History Details", historyDetails);

    const brandingData = useContext(BrandingContext);
    const { userInfo } = usePermissions();

    // Template configuration merge (from branding context if present)
    const prescriptionConfigItem = brandingData?.find(
      (item) =>
        item.name === "prescription_config" || item.name === "template_config"
    );
    const templateConfigFromContext = prescriptionConfigItem?.content
      ? typeof prescriptionConfigItem.content === "string"
        ? JSON.parse(prescriptionConfigItem.content)
        : prescriptionConfigItem.content
      : null;

    const prescriptionTemplateItem = brandingData?.find(
      (item) =>
        item.name === "prescription_template" || item.name === "template"
    );
    const templateBase64 =
      prescriptionTemplateItem?.content || userInfo?.template || null;

    // Merge template config: context API > userInfo > props > defaults
    const templateConfig = {
      showTopHeader:
        templateConfigFromContext?.showTopHeader ??
        userInfo?.template_config?.showTopHeader ??
        // showTopHeaderProp ??
        true,
      showFooter:
        templateConfigFromContext?.showFooter ??
        userInfo?.template_config?.showFooter ??
        // showFooterProp ??
        true,
      extraBottomMargin:
        templateConfigFromContext?.extraBottomMargin ??
        userInfo?.template_config?.extraBottomMargin ??
        0,
      extraRightMargin:
        templateConfigFromContext?.extraRightMargin ??
        userInfo?.template_config?.extraRightMargin ??
        0,
      extraLeftMargin:
        templateConfigFromContext?.extraLeftMargin ??
        userInfo?.template_config?.extraLeftMargin ??
        0,
      extraTopMargin:
        templateConfigFromContext?.extraTopMargin ??
        userInfo?.template_config?.extraTopMargin ??
        0,
      useBackgroundTemplate:
        templateConfigFromContext?.useBackgroundTemplate ??
        userInfo?.template_config?.useBackgroundTemplate ??
        false,
      printOfsetTop:
        templateConfigFromContext?.printOfsetTop ??
        userInfo?.template_config?.printOfsetTop ??
        0,
      printOfsetLeft:
        templateConfigFromContext?.printOfsetLeft ??
        userInfo?.template_config?.printOfsetLeft ??
        0,
    };

    const brandingItem = brandingData?.find(
      (item) => item.name === "branding_statements"
    );
    const collateralBrandStatement =
      brandingItem?.content?.collateralBrandStatement || "";
    const collateralImage =
      brandingData?.find((item) => item.name === "collateral_image")?.content ||
      "";

    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const baseMargin = 40;

    const {
      showTopHeader: configShowTopHeader = true,
      showFooter: configShowFooter = true,
      extraBottomMargin = 0,
      extraRightMargin = 0,
      extraLeftMargin = 0,
      extraTopMargin = 0,
      useBackgroundTemplate = false,
      printOfsetTop = 0,
      printOfsetLeft = 0,
    } = templateConfig;

    const EFFECTIVE_LEFT_MARGIN = baseMargin + Number(extraLeftMargin || 0);
    const EFFECTIVE_TOP_MARGIN = baseMargin + Number(extraTopMargin || 0);
    const PRINT_OFFSET_LEFT = Number(printOfsetLeft || 0);
    const PRINT_OFFSET_TOP = Number(printOfsetTop || 0);
    const EFFECTIVE_RIGHT_MARGIN = baseMargin + Number(extraRightMargin || 0);
    const CONTENT_BOTTOM_MARGIN = 80;
    const EXTRA_CONTENT_BOTTOM_MARGIN = Number(extraBottomMargin || 0);
    const EFFECTIVE_BOTTOM_MARGIN =
      CONTENT_BOTTOM_MARGIN + EXTRA_CONTENT_BOTTOM_MARGIN;

    const SHOW_TOP_HEADER = configShowTopHeader;
    const SHOW_FOOTER = configShowFooter;

    const INLINE_LABEL_COLUMN_WIDTH = 140;

    const hexToRgbArray = (hex) => {
      try {
        const bigint = parseInt(hex.replace("#", ""), 16);
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
      } catch (e) {
        return [238, 238, 238];
      }
    };

    const getImageType = (base64String) => {
      if (!base64String) return null;
      if (base64String.startsWith("/9j/")) return "jpeg";
      if (base64String.startsWith("iVBORw0KGgo")) return "png";
      return "jpeg";
    };

    const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
      if (!width || !height) return { width: maxWidth, height: maxHeight };
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const scale = Math.min(widthRatio, heightRatio, 1);
      return { width: width * scale, height: height * scale };
    };

    const ensureSpaceForSection = (doc, startY, minNeeded = 160) => {
      const available = pageHeight - startY - EFFECTIVE_BOTTOM_MARGIN;
      if (available < minNeeded) {
        doc.addPage();
        renderHeaderFooter({}, doc);
        return EFFECTIVE_TOP_MARGIN + 220 + PRINT_OFFSET_TOP;
      }
      return startY;
    };

    const renderHeaderFooter = (data, doc) => {
      const midX = pageWidth / 2 + 10 + PRINT_OFFSET_LEFT;
      const currentY = EFFECTIVE_TOP_MARGIN + PRINT_OFFSET_TOP;

      try {
        // optional watermark logic kept out for now
      } catch (e) {}

      doc.setFontSize(14);
      doc.setTextColor("#000");

      const clinicAddress = [
        sessionStorage.getItem("facilityAddressLine1"),
        sessionStorage.getItem("facilityAddressLine2"),
        sessionStorage.getItem("facilityAddressLine3"),
      ]
        .filter(Boolean)
        .join(", ");

      doc.setFontSize(10);

      const doctorY = currentY + 40;

      if (SHOW_TOP_HEADER) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(
          "Prescription",
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          currentY
        );
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        if (collateralImage) {
          const collateralImageDataUrl = collateralImage.startsWith("data:")
            ? collateralImage
            : `data:image/jpeg;base64,${collateralImage}`;
          try {
            const imageProps = doc.getImageProperties(collateralImageDataUrl);
            const { width: scaledWidth, height: scaledHeight } =
              getScaledDimensions(imageProps.width, imageProps.height, 110, 50);
            doc.addImage(
              collateralImageDataUrl,
              imageProps.fileType || "JPEG",
              pageWidth - scaledWidth - 60,
              currentY - 20,
              scaledWidth,
              scaledHeight
            );
          } catch (imgErr) {}
        }

        doc.text(
          `Dr. ${doctorData?.name}`,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          doctorY
        );
        doc.text(
          `Reg No: ${doctorData?.registration_no ?? "NA"}`,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          doctorY + 15
        );

        const qualificationsText = `${doctorData?.qualifications || ""}`;
        const wrappedQualifications = doc.splitTextToSize(
          qualificationsText,
          pageWidth / 2 - 40
        );
        doc.text(
          wrappedQualifications,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          doctorY + 30
        );

        const qualificationsLineCount = wrappedQualifications.length;
        const departmentY = doctorY + 30 + qualificationsLineCount * 12;

        const departmentText = `${doctorData?.department || ""}${
          doctorData?.specialization ? " - " + doctorData.specialization : ""
        }`;
        const wrappedDepartment = doc.splitTextToSize(
          departmentText,
          pageWidth / 2 - 40
        );
        doc.text(
          wrappedDepartment,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          departmentY
        );

        if (brandingItem?.content?.collateralBrandStatement) {
          doc.text(
            brandingItem.content.collateralBrandStatement,
            pageWidth - 40,
            doctorY,
            { align: "right" }
          );
        }

        const formattedDate = formatDateIST(new Date().toISOString());
        doc.text(`Date: ${formattedDate.split(" ")[0]}`, midX, doctorY + 15);
        doc.text(
          `Time: ${formattedDate.split(" ").slice(1).join(" ")}`,
          midX,
          doctorY + 30
        );

        const rightColumnWidth = Math.max(80, pageWidth - midX - 40);
        const clinicAddressLine = `Clinic Address: ${clinicAddress}`;
        const wrappedClinicAddress = doc.splitTextToSize(
          clinicAddressLine,
          rightColumnWidth
        );
        doc.text(wrappedClinicAddress, midX, doctorY + 45);
      }

      const patientY = doctorY + 85;
      const patientLine = [
        `Patient: ${patientData?.name}`,
        patientData?.age ? `Age: ${patientData.age} years` : null,
        patientData?.gender ? `Gender: ${patientData.gender}` : null,
        patientData?.patient_id ? `ID: ${patientData.patient_id}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.setFontSize(10);
      const availableWidth =
        pageWidth -
        (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT) -
        EFFECTIVE_RIGHT_MARGIN;
      const wrappedPatientLine = doc.splitTextToSize(
        patientLine,
        availableWidth
      );
      doc.text(
        wrappedPatientLine,
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        patientY
      );

      let patientSectionY = patientY + wrappedPatientLine.length * 12 + 5;

      const patientContactLine = [
        patientData?.phone_no ? `Phone: ${patientData.phone_no}` : null,
        selectedContext ? `Consultation ID: ${selectedContext}` : null,
      ]
        .filter(Boolean)
        .join(" | ");
      if (patientContactLine) {
        const wrappedContactLine = doc.splitTextToSize(
          patientContactLine,
          availableWidth
        );
        doc.text(
          wrappedContactLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );
        patientSectionY += wrappedContactLine.length * 12 + 5;
      }

      const abhaParts = [];
      if (patientData?.abha_id)
        abhaParts.push(`ABHA Address: ${patientData.abha_id}`);
      if (patientData?.abha_no)
        abhaParts.push(`ABHA Number: ${patientData.abha_no}`);
      if (abhaParts.length > 0) {
        const wrappedAbhaLine = doc.splitTextToSize(
          abhaParts.join(" | "),
          availableWidth
        );
        doc.text(
          wrappedAbhaLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );
        patientSectionY += wrappedAbhaLine.length * 12 + 5;
      }

      const separatorY = patientSectionY + 5;
      doc.line(
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        separatorY,
        pageWidth - EFFECTIVE_RIGHT_MARGIN,
        separatorY
      );
    };

    const renderFooter = (doc, pageNumber) => {
      if (!SHOW_FOOTER) return;
      const themeColour = "#EEEEEE";
      const margin = 5;

      const baseFooterHeight = 40;
      const dynamicFooterHeight = baseFooterHeight;

      const pageBottomY = pageHeight - margin;
      const footerTopY = pageBottomY - dynamicFooterHeight;

      const footerWidth =
        pageWidth - EFFECTIVE_LEFT_MARGIN - EFFECTIVE_RIGHT_MARGIN;
      const footerLeftX = EFFECTIVE_LEFT_MARGIN;

      doc.setFillColor(...hexToRgbArray(themeColour));
      doc.rect(footerLeftX, footerTopY, footerWidth, dynamicFooterHeight, "F");

      const lineY = footerTopY + dynamicFooterHeight / 2 + 3;
      const textLeftX = footerLeftX + 10;
      const logoWidth = 40;
      const logoHeight = 18;
      const logoX = footerLeftX + footerWidth - logoWidth - 10;

      const poweredText =
        "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (Visit: https://www.hlthtek.com)";
      doc.setFontSize(9);
      doc.setTextColor("#000000");
      doc.text(poweredText, textLeftX, lineY);

      if (NavLogoFooter) {
        try {
          doc.addImage(
            NavLogoFooter,
            "PNG",
            logoX,
            lineY - logoHeight + 6,
            logoWidth,
            logoHeight
          );
        } catch (e) {}
      }
    };

    const renderMedicationsTable = (doc, startY) => {
      if (
        !selectedSectionsData["Medications"] ||
        selectedSectionsData["Medications"].length === 0
      )
        return startY;

      doc.setFontSize(14);
      doc.setTextColor("#000");
      doc.text(
        "Medications",
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        startY
      );
      startY += 15;

      autoTable(doc, {
        startY: startY,
        head: [["Drug", "Frequency", "Route", "Duration", "Reason", "Note"]],
        body: selectedSectionsData["Medications"].map((item) => [
          item.term || "-",
          Array.isArray(item?.frequency)
            ? item?.frequency.map((o) => o.term).join(", ")
            : item?.frequency || "-",
          item.selectedAdministration?.label || "-",
          item.duration ? `${item.duration} ${item.durationType}` : "-",
          (item.reasons || []).join(", ") || "-",
          item.medicineNote || "-",
        ]),
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 250 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });

      return doc.lastAutoTable.finalY + 20;
    };
    const renderDoctorSignatureBlock = (doc, startY) => {
      const signature = doctorData?.doctor_signature;
      const imgType = signature ? getImageType(signature) : null;

      const blockWidth = 180; // Width of right-side signature block
      const blockX = pageWidth - blockWidth - EFFECTIVE_RIGHT_MARGIN; // right margin from config
      let y = startY;

      doc.setFontSize(11);
      doc.setTextColor("#000");

      // Signature Image (if available)
      if (signature) {
        doc.addImage(
          `data:image/${imgType};base64,${signature}`,
          imgType.toUpperCase(),
          blockX,
          y,
          140,
          60
        );
        y += 70;
      }

      // Doctor Name
      doc.setFont("helvetica", "bold");
      doc.text(`Dr. ${doctorData?.name}`, blockX, y);
      y += 14;

      doc.setFont("helvetica", "normal");

      // Qualifications (wrapped)
      if (doctorData?.qualifications) {
        const wrappedQual = doc.splitTextToSize(
          doctorData.qualifications,
          blockWidth
        );
        doc.text(wrappedQual, blockX, y);
        y += wrappedQual.length * 12;
      }

      // Department + Specialization
      const deptLine = `${doctorData?.department || ""}${
        doctorData?.specialization ? " - " + doctorData.specialization : ""
      }`;
      if (deptLine.trim().length > 0) {
        const wrappedDept = doc.splitTextToSize(deptLine, blockWidth);
        doc.text(wrappedDept, blockX, y);
        y += wrappedDept.length * 12;
      }

      // Registration Number
      doc.text(
        `Reg No: ${doctorData?.registration_no ?? "NA"}`,
        blockX,
        y + 10
      );

      return y + 30; // final Y pointer
    };

    const handleDownload = () => {
      return new Promise(async (resolve, reject) => {
        try {
          const doc = new jsPDF({
            orientation: "p",
            unit: "pt",
            format: "a4",
            compress: true,
          });

          const brandingItem = brandingData?.find(
            (item) => item.name === "branding_statements"
          );
          const themeColour = brandingItem?.content?.themeColour || "#21cdc0";

          let currentY = EFFECTIVE_TOP_MARGIN + 190 + PRINT_OFFSET_TOP + 15;

          currentY = renderMedicationsTable(doc, currentY);

          // Rendering doctor signature at the end
          currentY = ensureSpaceForSection(doc, currentY, 110);
          currentY = renderDoctorSignatureBlock(doc, currentY);

          // Add header/footer on all pages
          const totalPages = doc.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            renderHeaderFooter({}, doc); // add header
            if (SHOW_FOOTER) {
              renderFooter(doc, i); // add footer with correct page number
            }
          }

          // Post-process: optionally compose a new PDF with the template as background
          let finalBytes;
          if (useBackgroundTemplate) {
            try {
              const srcBytes = doc.output("arraybuffer");
              // const templateBytes = await fetch(templatePdfUrl4).then((r) =>
              //     r.arrayBuffer()
              // );
              const templateBytes = templateBase64
                ? Uint8Array.from(atob(templateBase64), (c) => c.charCodeAt(0))
                : await fetch(templatePdfUrl).then((r) => r.arrayBuffer());

              const newDoc = await PDFDocument.create();
              const srcDoc = await PDFDocument.load(srcBytes);
              const pageCount = srcDoc.getPageCount();

              for (let i = 0; i < pageCount; i++) {
                const [embeddedSrcPage] = await newDoc.embedPdf(srcBytes, [i]);
                const [embeddedTemplatePage] = await newDoc.embedPdf(
                  templateBytes,
                  [0]
                );

                const pageWidth = embeddedSrcPage.width;
                const pageHeight = embeddedSrcPage.height;

                const page = newDoc.addPage([pageWidth, pageHeight]);
                // Draw template first (background, full-bleed)
                page.drawPage(embeddedTemplatePage, {
                  x: 0,
                  y: 0,
                  width: pageWidth,
                  height: pageHeight,
                });
                // Draw generated jsPDF page content on top
                page.drawPage(embeddedSrcPage, {
                  x: 0,
                  y: 0,
                  width: pageWidth,
                  height: pageHeight,
                });
              }

              finalBytes = await newDoc.save();
            } catch (e) {
              // If overlay fails, fall back to plain jsPDF output
              console.error("PDF template overlay failed:", e);
              finalBytes = doc.output("arraybuffer");
            }
          } else {
            // No background template; use the jsPDF output directly
            finalBytes = doc.output("arraybuffer");
          }

          // const base64String = doc.output("datauristring").split(",")[1];
          // resolve(base64String);
          const base64String = arrayBufferToBase64(finalBytes);
          resolve(base64String);
        } catch (err) {
          reject(err);
        }
      });
    };
    function arrayBufferToBase64(buffer) {
      const bytes =
        buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(
          null,
          bytes.subarray(i, i + chunkSize)
        );
      }
      return btoa(binary);
    }

    useImperativeHandle(ref, () => ({
      generatePrescriptionPdf: () => handleDownload(),
    }));

    console.log("SectionsContent", selectedSectionsData);
    return <></>;
  }
);

export default React.memo(PrescriptionPdf);
